import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {useSchemaCollaboratorsSection} from 'collaborator-components/useSchemaCollaboratorsSection';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaWithOrgPermissionsSettings} from 'schemas/SchemaWithOrgPermissionsSettings';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  ConnectionSchemaPermissionSettingsQuery,
  ConnectionSchemaPermissionSettingsQueryVariables,
} from './__generated/ConnectionSchemaPermissionSettingsQuery';
import {updateCollaborators} from './ConnectionSchemaDAO';

type ConnectionSchema = ExtractByTypename<
  ConnectionSchemaPermissionSettingsQuery['connectionSchema'],
  'ConnectionSchemaNode'
>;

export const CONNECTION_SCHEMA_PERMISSION_SETTINGS_QUERY = gql`
  query ConnectionSchemaPermissionSettingsQuery($connectionSchemaId: StrictString!) @skipAudit {
    connectionSchema(id: $connectionSchemaId) {
      ... on PoliceableInterface {
        id
        permissions {
          ...SchemaPermissionsGraphQL
        }
        ...CollaboratorInfoGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('CollaboratorInfo').fragment}
  ${getFragmentWrapper('SchemaPermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function updateConnectionSchemaCollaborators(
  schemaId: string,
  data: {
    collaborators: Array<SchemaCollaborator>;
  }
): Promise<void> {
  const response = await updateCollaborators(schemaId, data);
  writeFragmentsToCache(getFragmentWrapper('CollaboratorInfo'), [response.schema]);
}

export interface QueryingProps {
  connectionSchemaId: string;
}
function QueryingConnectionSchemaPermissionSettings({connectionSchemaId}: QueryingProps): JSX.Element | null {
  const result = useBenchlingQuery<
    ConnectionSchemaPermissionSettingsQuery,
    ConnectionSchemaPermissionSettingsQueryVariables
  >(CONNECTION_SCHEMA_PERMISSION_SETTINGS_QUERY, {
    variables: {connectionSchemaId},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    assert(!isInaccessible(result.data.connectionSchema), 'Expected connection schema to be accessible.');
    return (
      <ExistingConnectionSchemaPermissionSettings
        connectionSchema={result.data.connectionSchema}
        refetch={async () => {
          await result.refetch();
        }}
      />
    );
  }
}

export function ExistingConnectionSchemaPermissionSettings({
  connectionSchema,
  refetch,
}: {
  connectionSchema: ConnectionSchema;
  refetch: () => Promise<void>;
}): JSX.Element {
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSection(
    connectionSchema.collaboratorsJson
  );
  return (
    <SchemaWithOrgPermissionsSettings
      id={connectionSchema.id}
      isReadOnly={!connectionSchema.permissions.definitionEditable}
      collaborators={collaborators}
      onUpdate={handleUpdate}
      onSubmit={async () => {
        await updateConnectionSchemaCollaborators(connectionSchema.id, {
          collaborators: minimalCollaborators,
        });
        await refetch();
      }}
    />
  );
}

export default QueryingConnectionSchemaPermissionSettings;
